import { useState, useRef, useId } from 'react';
import { useDismiss } from '@/shared/lib/hooks';
import { PasswordChangeModal } from '../PasswordChangeModal';
import * as s from './StatusHud.css';

export interface StatusHudProps {
  name: string;
  team: string;
  level?: string;
  isLoading?: boolean;
  isError?: boolean;
  accountBalances?: { id?: string; type: string; name: string; balance: number }[];
  onChangePassword?: (input: {
    currentPassword: string;
    newPassword: string;
  }) => Promise<void> | void;
  changePasswordLoading?: boolean;
  changePasswordError?: string;
  /**
   * 패스키 관리 패널 슬롯. 비밀번호 변경과 한 모달("비밀번호 · 패스키")의 두 번째 세그먼트로 들어간다 —
   * 둘 다 "기기당 한 번" 인 보안 항목이라 상태창 행을 하나로 합쳤다.
   */
  passkeyPanelSlot?: () => React.ReactNode;
  /**
   * 계정 설정 페이지 링크 슬롯 (예: MCP 토큰).
   * 라우터를 아는 호출부가 `a`(Link)로 렌더하고, HUD 는 액션 버튼과 같은 클래스만 넘긴다.
   */
  accountLinkSlot?: (props: { className: string }) => React.ReactNode;
  /**
   * 온보딩 안내 토글 슬롯 (onboarding-visibility-toggle CP1 T4).
   * 노출 판정(격리 아님 AND 완료됨)은 feature 컴포넌트가 스스로 한다 — HUD 는 자리만 내준다.
   */
  onboardingGuideSlot?: (props: { className: string }) => React.ReactNode;
  /** 로그아웃 — 모바일에선 HUD 가 유일한 로그아웃 경로 (데스크톱 CarouselDock 과 병행). */
  onLogout?: () => void;
}

export function StatusHud({
  name,
  team,
  level,
  isLoading = false,
  isError = false,
  accountBalances,
  onChangePassword,
  changePasswordLoading,
  changePasswordError,
  passkeyPanelSlot,
  accountLinkSlot,
  onboardingGuideSlot,
  onLogout,
}: StatusHudProps) {
  const [expanded, setExpanded] = useState(false);
  const [isSecurityOpen, setIsSecurityOpen] = useState(false);

  // ponytail: 패스키만 주입되면 라벨은 "패스키 관리" 인데 모달엔 비밀번호 폼도 뜬다.
  // 호출부(MainPage)는 둘 다 넘기므로 지금은 안 생기는 조합 — 생기면 모달 쪽에 폼 생략 prop 을 더한다.
  const securityLabel =
    onChangePassword && passkeyPanelSlot
      ? '비밀번호 · 패스키'
      : onChangePassword
        ? '비밀번호 변경'
        : '패스키 관리';

  const rootRef = useRef<HTMLDivElement>(null);

  const buttonId = useId();
  const panelId = useId();

  useDismiss(rootRef, () => setExpanded(false), { escape: true });

  const hasAssets = accountBalances != null && accountBalances.length > 0;
  const teamLabel = team || '미배정';
  const levelLabel = level || '인턴';

  const displayName = isLoading ? '데이터 수신 중' : isError ? '연결 실패' : name;

  function handleToggle() {
    setExpanded((prev) => !prev);
  }

  function handlePasswordComplete(input: { currentPassword: string; newPassword: string }) {
    void (async () => {
      try {
        await onChangePassword?.(input);
        setIsSecurityOpen(false);
      } catch {
        // 에러는 changePasswordError prop으로 표시
      }
    })();
  }

  return (
    <>
      <div ref={rootRef} className={s.root}>
        {/* Expanded panel — renders above compact */}
        <div className={`${s.expandedWrapper}${expanded ? ` ${s.expandedWrapperOpen}` : ''}`}>
          <div id={panelId} role="region" aria-labelledby={buttonId} className={s.expandedPanel}>
            {/* Asset balances */}
            {hasAssets && (
              <>
                <div className={s.assetsSection}>
                  {accountBalances.map((account) => (
                    <div key={account.id ?? account.type} className={s.assetRow}>
                      <span className={s.assetMarker}>◈</span>
                      <span className={s.assetName}>{account.name}</span>
                      <span className={s.assetBalance}>{account.balance.toLocaleString()}P</span>
                    </div>
                  ))}
                </div>
                <div className={s.divider} />
              </>
            )}

            {/* Account actions — 누르는 확률이 높을수록 아래(트리거·엄지 쪽)에 둔다.
                패널이 위로 열리므로 아래가 가깝다. 빈도는 계측이 아니라 항목 성격에서 나온 추정. */}
            <div className={s.actionsSection}>
              {(onChangePassword || passkeyPanelSlot) && (
                <button
                  type="button"
                  className={s.actionBtn}
                  onClick={() => setIsSecurityOpen(true)}
                >
                  {securityLabel}
                </button>
              )}
              {onboardingGuideSlot?.({ className: `${s.actionBtn} ${s.actionToggle}` })}
              {accountLinkSlot?.({ className: `${s.actionBtn} ${s.actionRoute}` })}
              {onLogout && (
                <button
                  type="button"
                  className={`${s.actionBtn} ${s.actionLeave}`}
                  onClick={onLogout}
                >
                  로그아웃
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Compact trigger (always visible) */}
        <button
          type="button"
          id={buttonId}
          className={s.compact}
          onClick={handleToggle}
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label="상태창 열기/닫기"
        >
          <div className={s.nameRow}>
            <span className={s.nameMarker}>▸</span>
            <span className={s.nameText}>{displayName}</span>
          </div>
          {isLoading ? (
            <div className={s.loadingLine} />
          ) : (
            <span className={s.teamLevelText}>
              {isError ? '재시도 필요' : `${teamLabel} · ${levelLabel}`}
            </span>
          )}
        </button>
      </div>

      <PasswordChangeModal
        open={isSecurityOpen}
        onClose={() => setIsSecurityOpen(false)}
        onComplete={handlePasswordComplete}
        loading={changePasswordLoading}
        error={changePasswordError}
        passkeyPanel={passkeyPanelSlot?.()}
      />
    </>
  );
}

export default StatusHud;
